import { logger } from '@/lib/logger';
import type { LessonPageData } from '@/supabase/services/lessonOptimized';
import { deleteOfflineFile, readOfflineText, snapshotKeyFor, snapshotRelativePath, writeOfflineText } from './paths';
import type { LessonSnapshot } from './types';

/**
 * Lesson snapshots: the lesson page payload (course, lesson, videos with
 * chapters, progress at download time) frozen to disk so the lesson screen can
 * render with no network. One snapshot per lesson, shared by all its videos.
 */
export function saveLessonSnapshot(courseSlug: string, lessonSlug: string, data: LessonPageData): string {
  const key = snapshotKeyFor(courseSlug, lessonSlug);
  const snapshot: LessonSnapshot = {
    key,
    savedAt: new Date().toISOString(),
    courseSlug,
    lessonSlug,
    // Materials are online-only (signed URLs, R2); drop them so the offline
    // page never renders links that cannot work.
    data: { ...data, materials: [] },
  };
  writeOfflineText(snapshotRelativePath(key), JSON.stringify(snapshot));
  return key;
}

export function loadLessonSnapshot(courseSlug: string, lessonSlug: string): LessonSnapshot | null {
  const key = snapshotKeyFor(courseSlug, lessonSlug);
  const raw = readOfflineText(snapshotRelativePath(key));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as LessonSnapshot;
    if (!parsed?.data?.lesson || !Array.isArray(parsed.data.videos)) return null;
    return parsed;
  } catch (err) {
    logger.warn('[offline] corrupt lesson snapshot, ignoring', { key, err });
    return null;
  }
}

export function deleteLessonSnapshot(key: string): void {
  deleteOfflineFile(snapshotRelativePath(key));
}
